import { AppUserRole } from '@dill/dill-shared';
import { AppRadioButton } from '../../../../../../general/AppRadioButton/AppRadioButton';

type CreditAppRole = 'CREDIT_APP_SALES' | 'CREDIT_APP_REVIEWER' | 'CREDIT_APP_DECISION_MAKER';
type ArRole = 'AR_SALES' | 'AR_ALL';
type AccessLevel = CreditAppRole | 'NO_ACCESS';
type ArAccessLevel = ArRole | 'NO_ACCESS';

function deriveAr(roles: AppUserRole[]): ArAccessLevel | null {
  if (roles.includes('AR_ALL')) return 'AR_ALL';
  if (roles.includes('AR_SALES')) return 'AR_SALES';
  return null;
}

export function deriveSelectedRoles(roles: AppUserRole[]): {
  creditApp: AccessLevel;
  ar: ArAccessLevel;
} {
  if (roles.length === 0) {
    return { creditApp: 'NO_ACCESS', ar: 'NO_ACCESS' };
  }
  const creditApp: AccessLevel = roles.includes('CREDIT_APP_DECISION_MAKER')
    ? 'CREDIT_APP_DECISION_MAKER'
    : roles.includes('CREDIT_APP_REVIEWER')
    ? 'CREDIT_APP_REVIEWER'
    : roles.includes('CREDIT_APP_SALES')
    ? 'CREDIT_APP_SALES'
    : 'NO_ACCESS';
  const ar: ArAccessLevel = deriveAr(roles) ?? 'NO_ACCESS';
  return { creditApp, ar };
}

export function deriveRolesFromSelection(creditApp: AccessLevel, ar: ArAccessLevel): AppUserRole[] {
  const base: AppUserRole[] = ['SUPPLIER', 'SUB_SUPPLIER'];
  if (creditApp !== 'NO_ACCESS') base.push(creditApp);
  if (ar !== 'NO_ACCESS') base.push(ar);
  return base;
}

interface SupplierUserRolesSectionProps {
  roles: AppUserRole[];
  onRolesChange: (roles: AppUserRole[]) => void;
}

const CREDIT_APP_OPTIONS: { value: AccessLevel; label: string; description: string }[] = [
  { value: 'NO_ACCESS', label: 'No Access', description: 'No access to credit app' },
  { value: 'CREDIT_APP_SALES', label: 'Sales', description: 'Only view credit app dashboard' },
  {
    value: 'CREDIT_APP_REVIEWER',
    label: 'Credit Analyst',
    description: 'View dashboard and review apps, cannot make decisions',
  },
  {
    value: 'CREDIT_APP_DECISION_MAKER',
    label: 'Credit Manager',
    description: 'View dashboard and review apps, can make decisions',
  },
];

const AR_OPTIONS: { value: ArAccessLevel; label: string; description: string }[] = [
  { value: 'NO_ACCESS', label: 'No Access', description: 'No access to AR' },
  {
    value: 'AR_SALES',
    label: 'Counter Sales / Dispatch',
    description: 'Only access to payment requests and Virtual POS',
  },
  { value: 'AR_ALL', label: 'Accounts Receivable', description: 'Access to all AR tabs' },
];

export const SupplierUserRolesSection = ({
  roles,
  onRolesChange,
}: SupplierUserRolesSectionProps) => {
  const { creditApp, ar } = deriveSelectedRoles(roles);

  const handleCreditAppChange = (value: AccessLevel) => {
    onRolesChange(deriveRolesFromSelection(value, ar));
  };

  const handleArChange = (value: ArAccessLevel) => {
    onRolesChange(deriveRolesFromSelection(creditApp, value));
  };

  return (
    <div className="space-y-4">
      {/* Credit App Roles */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Credit App Roles
          </span>
        </div>
        <div className="divide-y divide-gray-100">
          {CREDIT_APP_OPTIONS.map((option) => (
            <div
              key={option.value}
              className="flex items-center gap-3 px-3 py-2 cursor-pointer"
              onClick={() => handleCreditAppChange(option.value)}>
              <AppRadioButton isChecked={creditApp === option.value} />

              <div>
                <span className="text-xs font-medium text-gray-700">{option.label}</span>
                {option.description && (
                  <p className="text-[10px] text-gray-400">{option.description}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AR Roles */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            AR Roles
          </span>
        </div>
        <div className="divide-y divide-gray-100">
          {AR_OPTIONS.map((option) => (
            <div
              key={option.value}
              className="flex items-center gap-3 px-3 py-2 cursor-pointer"
              onClick={() => handleArChange(option.value)}>
              <AppRadioButton isChecked={ar === option.value} />
              <div>
                <span className="text-xs font-medium text-gray-700">{option.label}</span>
                {option.description && (
                  <p className="text-[10px] text-gray-400">{option.description}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
